import React, { useState, useEffect, useRef, useMemo } from 'react';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import moment from 'moment';
import { getDefaultTime } from '@/pages/intelligence/utils/topchartsUtils';
import {
  getTopList,
  getCalOtherList,
  getFixedTableData,
  MarketsType,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/countryUtils';
import {
  statsSteamSalesTrend,
  statsSteamSalesMarket,
} from '@/pages/intelligence/services/topcharts';
import { useSelector, getLocale } from 'umi';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { valueFormat, normalizeCountryMarketRows } from '@/pages/intelligence/utils/commonUtils';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { getItemFromArray } from '@/pages/intelligence/utils/tableUtils';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import PlayerbyCountryCountries from './components/PlayerbyCountry/Countries';
import useEarliestTime from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/useEarliestTime';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  isSteamPlayersByCountryMetric,
  resolveSteamSalesPeriod,
} from '@intelligence/pages/TopCharts/_config/playersByCountryMetrics';
import {
  DETAIL_PAGEID,
  getDetailPageCfg,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import CombineContent from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/components/StatsContext';
import { getSteamSalesTableList, buildTopCountriesBarChartData } from './_config/_utils';
import {
  getDefaultPageSortAttr,
  getIsFortyPage,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_utils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import useChartDownloadHooks from '@/pages/intelligence/pages/TopCharts/_hooks/useChartDownloadHooks';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import RegionSelector from '@intelligence/components/RegionSelector';
import { pcconsoleStats } from '@intelligence/v3/constants/downloadLogPoint';

import type { IRegion } from '@home/types/common';
import SteamSalesAdapter from './_config/steamSalesAdapter';
import { omit } from 'lodash-es';

const filterRegions = (regions?: IRegion[] | null, playerCountries?: any[] | null): IRegion[] => {
  // 输入验证
  if (!Array.isArray(regions) || regions.length === 0) return [];
  if (!Array.isArray(playerCountries) || playerCountries.length === 0) return [];

  // PlayerCounty指标数据的国家codes集合
  const playerCountryCodes = playerCountries
    .map((playerCountry) => playerCountry?.market?.toLowerCase() || '')
    .filter((marketCode) => !!marketCode);

  // 过滤区域国家
  return regions.reduce<IRegion[]>((acc, region) => {
    const markets = region?.country_set?.filter((market) => {
      const countryCode = market?.country_code || '';
      return !!countryCode && playerCountryCodes.includes(countryCode?.toLowerCase());
    });

    return !Array.isArray(markets) || markets.length === 0 ? acc : [...acc, { ...region, country_set: markets }];
  }, []);
};

// Players by country: 将占比小于 0.1% 的国家合并为 "Others"
// - 同时合并接口本身可能已经返回的 Others 行
// - 接口返回的 metric.value 是百分比值（0-100），所以阈值取 0.1
const PLAYER_COUNTRY_OTHERS_THRESHOLD_PERCENT = 0.1;

const isSteamSalesPriceMetric = (metric?: string): boolean =>
  metric === 'est_price' || !!metric?.endsWith('__est_price');

const isSteamSalesAcuMetric = (metric?: string): boolean =>
  metric === 'acu' || !!metric?.endsWith('__acu');

const isSteamSalesPcuMetric = (metric?: string): boolean =>
  metric === 'pcu' || !!metric?.endsWith('__pcu');

const isOthersMarketRow = (market: any): boolean => {
  if (typeof market !== 'string') return false;
  const lower = market.toLowerCase();
  return lower === 'others' || lower === 'other';
};

const getMetricValueByName = (item: any, name: string): number => {
  if (!item || !name) return 0;
  const metric = Array.isArray(item?.metrics)
    ? item.metrics.find((m: any) => m?.name === name)
    : null;
  const val = Number(metric?.value);
  return Number.isFinite(val) ? val : 0;
};

const mergeSmallPlayerCountriesIntoOthers = (
  list: any[],
  primaryMetricName: string,
): any[] => {
  if (!Array.isArray(list) || list.length === 0) return list || [];
  if (!primaryMetricName) return list;

  const regular: any[] = [];
  const toMerge: any[] = [];

  list.forEach((item) => {
    if (isOthersMarketRow(item?.market)) {
      toMerge.push(item);
      return;
    }
    if (
      getMetricValueByName(item, primaryMetricName) <
      PLAYER_COUNTRY_OTHERS_THRESHOLD_PERCENT
    ) {
      toMerge.push(item);
    } else {
      regular.push(item);
    }
  });

  if (toMerge.length === 0) return regular;

  // 汇总 toMerge 中所有 metric 字段（不止主指标），保证表格其他列也有合理求和
  const mergedMetricsMap: Record<
    string,
    { name: string; value: number; dod: any }
  > = {};
  toMerge.forEach((item) => {
    const metrics = Array.isArray(item?.metrics) ? item.metrics : [];
    metrics.forEach((m: any) => {
      const name = m?.name;
      if (!name) return;
      if (!mergedMetricsMap[name]) {
        mergedMetricsMap[name] = { name, value: 0, dod: null };
      }
      const v = Number(m?.value);
      if (Number.isFinite(v)) {
        mergedMetricsMap[name].value += v;
      }
    });
  });

  // 使用全小写 'others' 作为 market：
  // - useBarCharts 的 awaitChartIconUndateSeries 会把 market.toLowerCase() 作为 imageUrl key，
  //   x 轴 axisLabel 的 rich key 必须与之大小写一致，否则 Others 列不会拿到国旗 canvas 图。
  // - 表格列 CountriesShareColumn 已用 toLowerCase 检测 'others'，并通过 getCountryFlag('others')
  //   命中默认国旗占位。
  const othersRow: any = {
    market: 'others',
    name: 'Others',
    name_cn: '其他',
    metrics: Object.values(mergedMetricsMap),
  };

  return [...regular, othersRow];
};

// 根据指标获取对应的数据源
const getDataSourcesByMetric = (metric: string): string[] => {
  // alinea 数据源的指标以 alinea__ 开头
  if (metric?.startsWith('alinea__')) {
    return ['alinea'];
  }
  // gamalytic 数据源的指标以 gamalytic__ 开头
  if (metric?.startsWith('gamalytic__')) {
    return ['gamalytic'];
  }
  // source_steam 数据源的指标以 source_steam__ 开头
  if (metric?.startsWith('source_steam__')) {
    return ['source_steam'];
  }
  return ['vginsights'];
};

export default function SteamSales(props: any) {
  const {
    isBasicLoading,
    loading,
    setLoading,
    tabType,
    pageId,
    basicInfo,
    gameId,
    regionsCountries,
    entityType,
    isTwoMonths,
    activeSegment,
    mainSegment,
    subSegment,
    appIds,
    releaseInFortyDay,
    gsdDevicesFlatten,
    gsdSegments,
    filterLeftComp,
    filterCfg,
    newSteamSaleList,
    onDateTypeChange,
  } = props;

  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue || [];

  const pageCfg = getDetailPageCfg(DETAIL_PAGEID.steamData, tabType, entityType);
  const countriesByPlayerPageCfg = getDetailPageCfg(
    DETAIL_PAGEID.steamData,
    'countries',
    entityType,
  );
  const metricKey = subSegment || activeSegment;
  const getMetricDefaultPeriod = (metric: string) => {
    const metricOption = newSteamSaleList?.find((item: any) => item?.value === metric);
    const fallbackPeriod = isFortyDayTimeType ? 'daily' : 'monthly';
    return resolveSteamSalesPeriod(
      metric,
      metricOption?.dateEnum?.[0] || fallbackPeriod,
    );
  };
  const [metricSegVal, setMetricSegVal] = useState(metricKey);
  const defaultByDeviceVal = pageCfg?.defaultByDeviceVal || undefined;

  const [sumAvgMaxList, setSumAvgMaxList] = useState([]);
  const isStatsPage = tabType === 'stats';
  const isCountriesPage = tabType === 'countries';
  const infoItem = getItemFromArray({
    list: newSteamSaleList,
    key: 'value',
    keyVal: metricKey,
  });
  const { formatMessage } = useIntlExtend(); // 中英切换方法
  const locale = getLocale();
  const countryTypeRef = useRef('market');
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});
  const isFortyDay = getIsFortyPage(DETAIL_PAGEID.steamData, entityType, releaseInFortyDay);
  const isFortyDayTimeType = !isOperateDetail && isFortyDay;
  const [filterValue, setFilterVale] = useState(
    isOperateDetail ? detailFilterExtend : DEFAULT_FORM_VALUE,
  );

  const [fetchParams, setFetchParams] = useState<any>({
    period: getMetricDefaultPeriod(metricKey),
    start_date: '',
    end_date: '',
    data_sources: getDataSourcesByMetric(metricKey),
    // region_type: 'region',
    // regions: ['global'],
    entity_type: entityType,
    metrics:
      metricKey === 'est_revenue' || metricKey === 'est_units'
        ? ['est_revenue', 'est_units']
        : [metricKey],
    metric: metricKey,
    edition_id: gameId,
    b_group_by_device: defaultByDeviceVal,
  });

  useEffect(() => {
    onDateTypeChange?.(fetchParams.period);
  }, [fetchParams.period, onDateTypeChange]);

  useEffect(() => {
    setMetricSegVal(metricKey);
  }, [metricKey]);

  const [deviceBtn, setDeviceBtn] = useState(defaultByDeviceVal);
  const [deviceLineData, setDeviceLineData] = useState<any[]>([]);
  const [deviceBarData, setDeviceBarData] = useState<any[]>([]);
  const [devicePieData, setDevicePieData] = useState<any[]>([]);

  const [trendLineList, setTrendLineList] = useState<any>([]);
  const [trendTableList, setTrendTableList] = useState<any>([]);
  const [playerbyCountryList, setPlayerbyCountryList] = useState<any>([]);
  const [trendChartLoading, setTrendChartLoading] = useState(true);

  const [marketLineList, setMarketLineList] = useState<any>([]);
  const [marketTableList, setMarketTableList] = useState<any>([]);
  const [trendLoading, setTrendLoading] = useState(loading);
  const [trendChartType, setTrendChartType] = useState<string>(pageCfg?.defaultChartType || 'line');
  const [marketLoading, setMarketLoading] = useState(loading);
  const [countryLineLoading, setCountryLineLoading] = useState(true);
  const [topList, setTopList] = useState<any>(
    /* <TopListProp>*/ {
      total: {},
      global: {},
      globalWoChina: {},
      list: [],
    },
  );
  // 获取下架信息//TODO 从model里拿，存了没
  // const isPullOffGame = topchartsState.islowerGame; //下架的时间
  // const pullOffTime = topchartsState.lowerGameTime; //是否有下架时间的游戏
  // 掉榜游戏展示掉榜提示， 类似Nodata
  // const [isDropdown, setIsDropdown] = useState(false);
  //   trend标题加上数据聚合方式
  const [trendComputeType, setTrendComputeType] = useState('');
  const [trendCount, setTrendCount] = useState<string | number>('');
  // const [earliestPeriod, setEarliestPeriod] = useState('');
  const [trendError, setTrendError] = useState(false);
  const [marketError, setMarketError] = useState(false);
  const [marketChartType, setMarketChartType] = useState<string>(
    pageCfg?.defaultChartType || 'line',
  );
  const [marketFixedTableData, setMarketFixedTableData] = useState<any[]>([]);

  const [playerCountryData, setPlayerCountryData] = useState<any[]>([]); // playerbycountry数据
  const [playerCountryBarData, setPlayerCountryBarData] = useState<any[]>([]); // playerbycountry数据
  const [countryLatestDate, setCountryLatestDate] = useState('');
  const [regionSelectValue, setRegionSelectValue] = useState({
    regions: [],
    markets: ['global'],
    type: 'market',
  });
  const [filterData, setFilterData] = useState<any>({
    tableList: [],
    barList: [],
  });
  const [hideGlobal, setHideGlobal] = useState(false);
  const isPlayerbyCountry =
    isSteamPlayersByCountryMetric(metricKey) ||
    metricKey === 'alinea__wishlists_by_country';
  const rawPlayerCountryRef = useRef<{ table: any[]; charts: any[] }>({
    table: [],
    charts: [],
  });

  const applyPlayerCountryData = (
    rawTable: any[],
    rawCharts: any[],
    latestTime?: string,
  ) => {
    const tableList = normalizeCountryMarketRows(rawTable, regionsCountries);
    const tableBarList = normalizeCountryMarketRows(rawCharts, regionsCountries);
    const topListTmp = getTopList(tableList);
    const topListBarTmp = getTopList(tableBarList);
    const mergedTableList = mergeSmallPlayerCountriesIntoOthers(
      topListTmp?.list,
      metricKey,
    );
    const mergedBarList = buildTopCountriesBarChartData(
      mergeSmallPlayerCountriesIntoOthers(topListBarTmp?.list, metricKey),
      metricKey,
    );
    setPlayerCountryData(mergedTableList);
    setPlayerCountryBarData(mergedBarList);
    setFilterData({ tableList: mergedTableList, barList: mergedBarList });
    setTopList(topListTmp);
    setMarketFixedTableData(
      tableList.length ? getFixedTableData(marketType, topListTmp) : [],
    );
    if (latestTime) setCountryLatestDate(latestTime);
  };

  useEffect(() => {
    if (!isPlayerbyCountry || !regionsCountries?.length) return;
    const { table, charts } = rawPlayerCountryRef.current;
    if (table?.length) applyPlayerCountryData(table, charts);
  }, [regionsCountries, isPlayerbyCountry, metricKey]);

  const isStepLine =
    fetchParams.period === 'daily' &&
    (metricKey === 'est_price' ||
      metricKey === 'weighted_price' ||
      metricKey === 'lifetime_weighted_price' ||
      metricKey === 'alinea__est_price' ||
      metricKey === 'alinea__weighted_price' ||
      metricKey === 'alinea__lifetime_weighted_price');

  // 获取计算总数的类型字段
  const [marketType, setMarketType] = useState(MarketsType.Global);

  // 获取 Steam Sales Trend 数据（完整流程：参数转换 + API 调用 + 响应转换）
  const statsSteamSalesTrendPlus = async (entityType: IEntityType, params: any) => {
    if (entityType === 'combine') {
      return SteamSalesAdapter.fetchSalesTrend(params);
    }

    return statsSteamSalesTrend(params);
  };

  // 获取 Steam Sales Market 数据（完整流程：参数转换 + API 调用 + 响应转换）
  const statsSteamSalesMarketPlus = async (entityType: IEntityType, params: any) => {
    if (entityType === 'combine') {
      return SteamSalesAdapter.fetchSalesMarket(params);
    }

    return statsSteamSalesMarket(params);
  };

  // 获取可视化下载配置hooks
  const { chartDownloadConfig, setSumList, showDropdownBtnListCur, setTrendsChartDownList } =
    useChartDownloadHooks({
      locale,
      metricValue: activeSegment,
      entityType,
      activeSegment,
    });

  const {
    earliestPeriod,
    formattedEarliestDay,
    updateEarliestDateData,
    baseReleaseTimeValue,
    baseReleaseTimeData,
  } = useEarliestTime({
    entityType,
    basicInfo,
    appIds,
    dateType: fetchParams.period,
    topchartsState,
  });

  useEffect(() => {
    // 是否请求完basic接口
    if (isBasicLoading) {
      // 选择单appid时要重新loading
      setLoading(true);
    } else {
      let newFetchParams = {
        ...fetchParams,
        period: getMetricDefaultPeriod(metricKey),
        data_sources: getDataSourcesByMetric(metricKey),
        metrics:
          metricKey === 'est_revenue' || metricKey === 'est_units'
            ? ['est_revenue', 'est_units']
            : [metricKey],
        metric: metricKey,
      };

      const { filterValue: extendFilter, filterParams } = getDbiFilterParams({
        // urlFilter: any,
        filterSaveData: null,
        filterExtend: detailFilterExtend,
        specialParams: filterCfg?.specialParams,
        isOperate: isOperateDetail,
        filterName: '',
        filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
      });

      if (isOperateDetail) {
        // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
        setDetailFilterExtend(extendFilter);
      }
      setFilterVale(extendFilter);
      newFetchParams = { ...newFetchParams, ...filterCfg?.handleFilterToApiParams(filterParams) };

      getPageData(newFetchParams);

      updateEarliestDateData({
        dataSources: getDataSourcesByMetric(metricKey),
        metrics: [metricKey],
        regionType: 'region',
        regions: ['global'],
        dateType: getMetricDefaultPeriod(metricKey),
      });
    }
  }, [isBasicLoading, pageId]);

  const getPageData = (newFetchParams: any) => {
    if (isStatsPage) {
      if (isPlayerbyCountry) {
        getCountry(newFetchParams, countryTypeRef.current);
      } else {
        getStatsData(newFetchParams);
      }
    } else if (isCountriesPage) {
      getCountry(newFetchParams, countryTypeRef.current);
    } else {
      console.error(`tabType=${tabType} is not stats||countries!`);
    }
  };

  const getStatsData = async (params = fetchParams) => {
    const newFetchParams = { ...fetchParams, ...params };
    setFetchParams(newFetchParams);
    try {
      /** 接口速率优化：
       * 第一次时间为空时先请求trend获取时间后再请求channel；
       * 如果时间不为空，则可以同时请求2个接口
       */
      const { start_date, end_date } = await getTrends(newFetchParams);
      if (start_date && end_date) {
        newFetchParams.start_date = start_date;
        newFetchParams.end_date = end_date;
      } else {
        const [defautStart, defautEnd] = getDefaultTime(newFetchParams.period, isTwoMonths);
        newFetchParams.start_date = defautStart;
        newFetchParams.end_date = defautEnd;
      }
      setFetchParams(newFetchParams);
    } catch (err) {
      console.error(err);
      setTrendLoading(false);
    }
  };

  const getTrends = async (params: any) => {
    setTrendLoading(true);
    setLoading(true);
    const newFetchParams = { ...fetchParams, ...params };
    setFetchParams(newFetchParams);
    setTrendTableList([]);
    // setReqParamsDownLoad({ ...newFetchParams });
    try {
      const trendRes = await statsSteamSalesTrendPlus(entityType, newFetchParams);
      // let avgValue = trendRes?.data?.metric_total;
      // setTrendComputeType('Sum');
      // setTrendCount(avgValue === null ? '-' : valueFormat(avgValue, false, 0));
      const sumAvgList = trendRes?.data?.sum_avg || [];
      // 如果是需要提供可视化下载的metirc，需要调用此hooks方法，提供对应config数据
      if (Array.isArray(sumAvgList) && sumAvgList?.length > 0) {
        setSumList(sumAvgList);
      }
      setTrendComputeType('Sum');
      const countData = Array.isArray(trendRes?.data?.total) ? trendRes?.data?.total : [];
      const activeItem: any = countData?.find(
        (item: any) => item?.metric === subSegment && item?.opt === 'sum',
      );
      setTrendCount(valueFormat(activeItem?.value, false, 0).toString());

      const startDate = trendRes?.req?.start_date;
      const endDate = trendRes?.req?.end_date;
      const reqApi = {
        ...newFetchParams,
        start_date: startDate,
        end_date: endDate,
      };
      // setIsDropdown(trendRes?.data?.b_drop_global || false);
      setFetchParams({ ...reqApi });

      const lineList = trendRes?.data?.trend || [];
      const lineListTemp = lineList.sort(
        (a: any, b: any) => moment(a.date).valueOf() - moment(b.date).valueOf(),
      );
      setTrendLineList(lineListTemp);
      setTrendsChartDownList(lineListTemp);
      if (isPlayerbyCountry) {
        setPlayerbyCountryList(lineListTemp);
      } else {
        setTrendTableList(lineList);
      }
      setDeviceLineData(trendRes?.data?.device_trends || []);
      if (entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE) {
        setSumAvgMaxList(trendRes?.data?.sum_avg || []);
      }

      // 给时间筛选器赋值
      // setDateTimeDatas('monthly', startDate, endDate);
      setTrendLoading(false);
      setLoading(false);
      setTrendError(false);
      return {
        start_date: startDate,
        end_date: endDate,
      };
    } catch (err) {
      setSumAvgMaxList([]);
      setTrendLineList([]);
      setTrendTableList([]);
      setPlayerbyCountryList([]);
      // setDateTimeDatas('monthly', '', '');
      setTrendLoading(false);
      setLoading(false);
      setTrendError(true);
      return {
        start_date: '',
        end_date: '',
      };
    }
  };

  const transLineData = (data: any[], metric: string = metricKey) => {
    const newData = data?.map((item: any) => {
      const newHistory = item?.history?.map((_history: any) => {
        const currentItem = _history?.metrics?.find((t: any) => t?.metric === metric);
        return {
          ..._history,
          value: currentItem?.value,
        };
      });
      const newItem = {
        ...item,
        history: newHistory,
      };
      return newItem;
    });
    return newData;
  };
  const regionsNew = useMemo(() => {
    return filterRegions(regionsCountries, playerCountryData);
  }, [regionsCountries, playerCountryData]);
  const getCountry = async (params: any, countryType: string) => {
    const newFetchParams = {
      ...fetchParams,
      ...params,
    };
    if (isPlayerbyCountry) {
      newFetchParams.start_date = '';
      newFetchParams.end_date = '';
    }

    setFetchParams(newFetchParams);
    // setReqParamsDownLoad({ ...newFetchParams });
    setLoading(true);
    setMarketLoading(true);
    try {
      let tableList = [];
      let tableBarList = [];
      const countryRes = await statsSteamSalesMarketPlus(entityType, newFetchParams);
      if (isPlayerbyCountry) {
        const rawTable = countryRes?.data?.players_country_table || [];
        const rawCharts = countryRes?.data?.players_country_charts || [];
        rawPlayerCountryRef.current = { table: rawTable, charts: rawCharts };
        applyPlayerCountryData(rawTable, rawCharts, countryRes?.data?.time);
        tableList = [];
        tableBarList = [];
      } else {
        tableList = normalizeCountryMarketRows(
          countryRes?.data?.table || [],
          regionsCountries,
        );
        if (
          isSteamSalesPriceMetric(metricKey) ||
          isSteamSalesAcuMetric(metricKey) ||
          isSteamSalesPcuMetric(metricKey)
        ) {
          const metricStats: Record<string, { sum: number; count: number; max: number }> = {};

          countryRes?.data?.trend?.forEach((row: any) => {
            row?.history?.forEach((historyItem: any) => {
              historyItem?.metrics?.forEach((metric: any) => {
                const metricName = metric?.metric || metric?.name;
                const value = Number(metric?.value);
                if (!Number.isFinite(value)) return;
                const key = `${row?.market || ''}__${metricName}`;
                const stat = metricStats[key] || { sum: 0, count: 0, max: value };
                metricStats[key] = {
                  sum: stat.sum + value,
                  count: stat.count + 1,
                  max: Math.max(stat.max, value),
                };
              });
            });
          });

          tableList = tableList.map((row: any) => ({
            ...row,
            metrics: row?.metrics?.map((metric: any) => {
              const metricName = metric?.metric || metric?.name || metricKey;
              const value = Number(metric?.value);
              if (metric?.value == null || !Number.isFinite(value)) return metric;
              const stat = metricStats[`${row?.market || ''}__${metricName}`];
              if (isSteamSalesPcuMetric(metricName)) {
                return { ...metric, value: stat?.max ?? metric.value };
              }
              if (isSteamSalesPriceMetric(metricName) || isSteamSalesAcuMetric(metricName)) {
                return { ...metric, value: stat?.count ? stat.sum / stat.count : metric.value };
              }
              return metric;
            }),
          }));
        }
      }
      let startDate = '';
      let endDate = '';

      if (countryRes?.code === 0 && !!countryRes?.data) {
        if (!isPlayerbyCountry) {
          const topListTmp = getTopList(tableList);
          const firstFixedData = !tableList.length
            ? []
            : getFixedTableData(marketType, topListTmp);
          setMarketTableList(topListTmp.list);
          setTopList(topListTmp);
          setMarketFixedTableData(firstFixedData);

          const lineData = transLineData(countryRes?.data?.trend, 'est_revenue');
          setMarketLineList(lineData);
        }

        startDate = countryRes?.req?.start_date;
        endDate = countryRes?.req?.end_date;
        if (startDate && endDate) {
          newFetchParams.start_date = startDate;
          newFetchParams.end_date = endDate;
        } else {
          const [defautStart, defautEnd] = getDefaultTime(newFetchParams.period, isTwoMonths);
          newFetchParams.start_date = defautStart;
          newFetchParams.end_date = defautEnd;
        }
        setFetchParams(newFetchParams);
        // setMarketTotalList(totalList || []);
      } else {
        setMarketLineList([]);
        setMarketTableList([]);
      }
      setMarketError(false);
    } catch (err) {
      console.error(err);
      setMarketLineList([]);
      setMarketTableList([]);
      setMarketError(true);
    }
    setMarketLoading(false);
    setLoading(false);
  };

  const handleConfirm = (value: any) => {
    const newFetchParams = {
      ...fetchParams,
      ...value,
      ...(isSteamPlayersByCountryMetric(metricKey)
        ? { period: resolveSteamSalesPeriod(metricKey) }
        : {}),
    };
    const shouldFetch = onDateTypeChange?.(newFetchParams.period) !== false;
    if (!shouldFetch) {
      setFetchParams(newFetchParams);
      return;
    }

    // 获取页面接口
    getPageData(newFetchParams);

    updateEarliestDateData({
      dataSources: getDataSourcesByMetric(metricKey),
      metrics: [metricKey],
      regionType: 'region',
      regions: ['global'],
      dateType: newFetchParams.period,
    });
  };

  const getDownloadFetchParams = () => {
    const paramsTmp: {
      [key: string]: any;
    } = {
      ...fetchParams,
      segment: pageId,
      unified_id: gameId,
      unified_ids: [gameId],
      currentMetric: ['revenue'].includes(mainSegment) ? subSegment : mainSegment,
      subSegment,
      region_type: 'region',
      regionType: 'region',
      regions: ['global'],
      isCountriesPage,
    };
    if (entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE)
      return {
        ...paramsTmp,
        device: ['PC'],
        source: paramsTmp?.data_sources?.[0] || '',
        sort_field: paramsTmp?.metric || '',
      };
    return paramsTmp;
  };

  const getDownloadFetchParamsForCombineSteamData = () => {
    const removeKeys = [
      'b_true_data',
      'b_group_by_date',
      'b_growth_ratio',
      'b_sum_avg_show',
      'b_group_by_market',
      'b_group_by_region',
      'b_group_by_platform',
      'b_group_by_segment',
      'start_release_time',
      'end_release_time',
    ];
    let fetchParamsNew = {
      ...fetchParams,
    };
    fetchParamsNew = SteamSalesAdapter.processGamalyticMetrics(fetchParamsNew);
    fetchParamsNew = SteamSalesAdapter.convertSteamSalesTrendsParams(fetchParamsNew);
    fetchParamsNew = omit(fetchParamsNew, removeKeys);

    return {
      ...fetchParams,
      ...fetchParamsNew,
      source: fetchParams?.data_sources?.[0] || '',
      subSegment: fetchParams?.metric || subSegment,
      currentMetric: 'steamSales',
      unified_ids: fetchParamsNew?.ids || [],
      regions: fetchParamsNew?.markets,
      isCountriesPage,
    };
  };

  const getDownloadFetchParamsNew = () => {
    if (entityType === 'combine') {
      return getDownloadFetchParamsForCombineSteamData();
    }

    return getDownloadFetchParams();
  };

  const pageSortAttr = getDefaultPageSortAttr(isStatsPage, 'desc', fetchParams?.metric);

  const getCardStatus = (tabType: string) => {
    // Players by country（包含 vgi / gamalytic）走的是 players_country_table / players_country_charts
    // 不会填充 marketTableList，所以 noData 需要基于 playerCountryData / filterData 判断
    if (isPlayerbyCountry) {
      const hasTable = Array.isArray(filterData?.tableList) && filterData.tableList.length > 0;
      const hasBar = Array.isArray(filterData?.barList) && filterData.barList.length > 0;
      return {
        isLoading: loading || marketLoading,
        isError: marketError,
        isNoData: !hasTable && !hasBar,
      };
    }
    if (isCountriesPage || isPlayerbyCountry) {
      return {
        isLoading: loading || marketLoading,
        isError: marketError,
        isNoData: !marketTableList?.length && !marketFixedTableData?.length,
      };
    }
    return {
      isLoading: loading || trendLoading,
      isError: trendError,
      isNoData: !trendTableList?.length,
    };
  };

  const showSumValue = infoItem?.value === 'est_revenue' || infoItem?.value === 'est_units';

  const subTitle =
    entityType !== PC_CONSOLE_COMBINE_ENTITY_TYPE && isStatsPage && showSumValue
      ? `${trendComputeType} ${formatMessage({
          id: /* infoItem?.textDetail ||*/ infoItem?.text,
        })} : ${trendCount}`
      : '';

  const getNodataMessage = () => {
    // 只调整PCU/mscience跟vgi
    if (isFortyDay && fetchParams?.period === 'monthly' && isStatsPage) {
      return formatMessage({ id: 'dbi_info_fortyday_nodatamsg' });
    }
    return formatMessage({ id: 'common_nodata' });
  };
  const filterTableData = (tableData: any[], markets: string[]) => {
    // 过滤出选中市场的国家行（不带预生成 Others，因为 Others 不属于任何具体市场）
    const filtered = tableData.filter((item: any) => {
      const code = item?.market?.toLocaleLowerCase();
      return markets.includes(code);
    });
    // 在过滤后的数据中重新按 0.1% 阈值合并出 Others
    return mergeSmallPlayerCountriesIntoOthers(filtered, metricKey);
  };
  const filterBarData = (barData: any[], markets: string[]) => {
    const filteredItems: any[] = barData.filter((item: any) => {
      const code = item?.market?.toLocaleLowerCase();
      return markets?.includes(code);
    });

    const mergedAll = mergeSmallPlayerCountriesIntoOthers(filteredItems, metricKey);
    return buildTopCountriesBarChartData(mergedAll, metricKey);
  };
  const hanldeRegionSelect = (val: any) => {
    setRegionSelectValue(val);
    const { realMarkets, markets } = val || {};
    const needFilterData = realMarkets?.length && !markets.includes('global');
    setFilterData({
      tableList: needFilterData ? filterTableData(playerCountryData, markets) : playerCountryData,
      barList: needFilterData ? filterBarData(playerCountryData, markets) : playerCountryBarData,
    });
    setHideGlobal(needFilterData);
  };
  useEffect(() => {
    if (isPlayerbyCountry) {
      setRegionSelectValue({
        regions: [],
        markets: ['global'],
        type: 'market',
      });
    }
  }, [isPlayerbyCountry]);
  return (
    <>
      <div className="dbi-statsfilter-warp">
        <div className="statsfilter-warp-left">
          {filterLeftComp}
          {isPlayerbyCountry ? (
            <RegionSelector
              key={'region'}
              locale={locale}
              disableNoChina={true}
              defaultValue={{
                regions: [],
                markets: ['global'],
                type: 'market',
              }}
              loading={loading}
              onConfirm={hanldeRegionSelect}
              options={regionsNew}
              value={regionSelectValue}
              entityType={entityType}
              showRadioBubble={true}
            />
          ) : null}

          {!isPlayerbyCountry && (
            <>
              <Filter
                timeFilterValues={{
                  type: fetchParams.period,
                  value: [fetchParams.start_date, fetchParams.end_date],
                }}
                filterConfirm={handleConfirm}
                filterReset={() => {}}
                sinceReleaseTime={baseReleaseTimeValue}
                earliestDataTime={earliestPeriod}
                filterValue={filterValue}
                setFilterVale={setFilterVale}
                filterCfg={filterCfg}
                defaultValue={DEFAULT_FORM_VALUE}
              />
              <span className="dbi-statsfilter-warp-left-text">
                {formatMessage({ id: 'dbi_steam_only' })}
              </span>
            </>
          )}
        </div>
        <div className="dbi-statsfilter-warp-right">
          <ExportTable
            entityType={entityType}
            tableParams={getDownloadFetchParamsNew()}
            isCoutriesPage={isCountriesPage}
            isDisableDownload={loading}
            isShowMaxDownloadRowsTips={false}
            downloadModalSize={820}
            isDisableDataAmount={true}
            newStyleFileName={`${basicInfo?.name}${
              isStatsPage ? '_Stats' : isCountriesPage ? '_Countries' : ''
            }`}
            gsdDevicesFlatten={gsdDevicesFlatten}
            gsdSegments={gsdSegments}
            showDropdownBtnList={showDropdownBtnListCur}
            chartDownloadConfig={chartDownloadConfig}
            tabType={tabType}
            useWebDownload={entityType === 'combine'}
            businessType={
              entityType === 'combine'
                ? 'combine-detail-allmetrics'
                : `${entityType}-stats-countries`
            }
            downloadKey="combineStatsSteamData"
            downloadLogConfigKey={pcconsoleStats?.[`${entityType}_${tabType}_${pageId}`]}
          />
        </div>
      </div>
      {isPlayerbyCountry ? (
        <PlayerbyCountryCountries
          pageId={pageId}
          tableData={filterData.tableList}
          playerCountryBarData={filterData.barList}
          cardStatus={getCardStatus('countries')}
          chartType={marketChartType}
          setChartType={setMarketChartType}
          chartLoading={countryLineLoading}
          setChartLoading={setCountryLineLoading}
          chartSegment={metricKey}
          dateType={fetchParams.period}
          countryLatestDate={countryLatestDate}
          regionsCountries={regionsCountries || []}
          topList={topList}
          fixedTableData={hideGlobal ? [] : marketFixedTableData}
          infoItem={infoItem}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          pageCfg={{
            ...(countriesByPlayerPageCfg || {}),
            tableMetricList: getSteamSalesTableList(infoItem?.value, newSteamSaleList),
          }}
          entityType={entityType}
        />
      ) : (
        <CombineContent
          subTitle={entityType !== PC_CONSOLE_COMBINE_ENTITY_TYPE && isStatsPage ? subTitle : ''}
          pageId={pageId}
          entityType={entityType}
          isCountriesPage={isCountriesPage}
          fixedTableData={marketFixedTableData}
          tableData={isCountriesPage ? marketTableList : trendTableList}
          sumAvgMaxList={sumAvgMaxList}
          lineData={isCountriesPage ? marketLineList : trendLineList}
          barData={isCountriesPage ? marketFixedTableData : []}
          pieData={isCountriesPage ? marketFixedTableData : []}
          stackBarData={deviceBarData}
          stackLineData={deviceLineData}
          stackPieData={devicePieData}
          calOtherList={getCalOtherList(marketType, topList)}
          chartType={isCountriesPage ? marketChartType : trendChartType}
          setChartType={isCountriesPage ? setMarketChartType : setTrendChartType}
          metricSegVal={metricSegVal}
          setMetricSegVal={(value: string) => {
            setMetricSegVal(value);
          }}
          cardStatus={getCardStatus(tabType)}
          earliestDay={formattedEarliestDay}
          baseReleaseTime={baseReleaseTimeData}
          firstWeekReleaseTime={baseReleaseTimeValue}
          earliestPeriod={earliestPeriod}
          defaultByDeviceVal={defaultByDeviceVal}
          dateType={fetchParams?.period}
          startDate={fetchParams?.start_date}
          endDate={fetchParams?.end_date}
          pageSortAttr={pageSortAttr}
          pageCfg={{
            ...(pageCfg || {}),
            tableMetricList: getSteamSalesTableList(infoItem?.value, newSteamSaleList),
            getMetricValueOfCvm: (cvm, metricKey) =>
              pageCfg?.getMetricValueOfCvm?.(cvm, metricKey, 'metric'),
          }}
          cardTitle={
            isStatsPage && infoItem?.text
              ? ['est_price', 'weighted_price', 'lifetime_weighted_price', 'alinea__est_price', 'alinea__weighted_price', 'alinea__lifetime_weighted_price'].includes(metricKey)
                ? formatMessage({ id: infoItem?.text })
                : `${formatMessage({ id: infoItem?.text })}${
                    locale === 'zh-CN' ? '' : ' '
                  }${formatMessage({ id: `dbi_detail_trend` })}`
              : ''
          }
          chartLoading={isCountriesPage ? countryLineLoading : trendChartLoading}
          setChartLoading={isCountriesPage ? setCountryLineLoading : setTrendChartLoading}
          visualisMetric={metricKey}
          visualisTitleNow={formatMessage({ id: infoItem?.text })}
          isStepLine={isStepLine}
          gameName={basicInfo?.name}
          gameCover={basicInfo?.cover}
          changeDeviceBtn={(val: boolean) => {
            setDeviceBtn(val);
          }}
          regionsCountries={regionsCountries || []}
          isPercentage={infoItem?.renderType === RenderType.GROWTH_ONLY}
          noDataMessage={getNodataMessage()}
          mainSegment={mainSegment}
          ifLineChartLegendOn={showDropdownBtnListCur}
        />
      )}
    </>
  );
}
